import { MEMBERSHIP_DISCOUNT_CONDITIONS } from 'src/models/membership-type/MembershipDiscount'
import { CONDITIONS as MEMBERSHIP_POINT_CONDITIONS } from 'src/models/membership-type/MembershipPoint'
import MembershipType, { MEMBERSHIP_TIER_CONDITIONS_TERMS } from 'src/models/membership-type/MembershipType'
import { MEMBERSHIP_VOUCHER_CONDITIONS } from 'src/models/membership-type/MembershipVoucher'
import Model, { encoders, ID, Integer, JsonArray, JsonObject, MODEL_LEVEL } from 'src/models/model'

export const MEMBERSHIP_TIER_DESCRIPTION_TYPES = {
  PREFIX: 'prefix',
  REPLACE: 'replace'
}
MEMBERSHIP_TIER_DESCRIPTION_TYPES.ALL = _.values(MEMBERSHIP_TIER_DESCRIPTION_TYPES)

export const MEMBERSHIP_TIER_CONDITIONS = {
  VISITS: 'visits',
  TICKETS: 'tickets',
  MOVIES: 'movies',
  POINTS: 'points',
  POINTS_BALANCE: 'points_balance'
}
MEMBERSHIP_TIER_CONDITIONS.ALL = _.values(MEMBERSHIP_TIER_CONDITIONS)

export default class MembershipTier extends Model {
  static name = 'MembershipTier'

  static paginate = true
  static versioned = true

  static level = MODEL_LEVEL.CIRCUIT

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Site]

  static define () {
    this.fields = {
      id: {
        type: ID
      },
      name: {
        type: String,
        sortable: true,
        translatable: true
      },
      membershipType: {
        type: MembershipType,
        encode: encoders.hasOne,
        reference: true,
        output: true
      },
      descriptionType: {
        type: String,
        default: 'prefix'
      },
      description: {
        type: String,
        default: '',
        translatable: true
      },
      priorityOrder: {
        type: Integer,
        default: 100
      },
      conditions: {
        type: JsonArray,
        default: () => ([]),
        required: false
      },
      conditionsRequirements: {
        type: JsonObject,
        default: () => ({
          visits: 1,
          tickets: 1,
          movies: 1,
          points: 1,
          points_balance: 1
        }),
        required: false
      },
      color: {
        type: String,
        default: '#027be3',
        required: false
      }
    }

    this.computed = {
      nameWithMembershipType () {
        if (!_.isNil(this.membershipType)) {
          return `${this.name} (${this.membershipType.name})`
        }

        return this.name
      },
      isConditional () {
        return !!this.conditions.length
      },
      requiresVisits () {
        return _.includes(this.conditions, MEMBERSHIP_TIER_CONDITIONS.VISITS)
      },
      requiresTickets () {
        return _.includes(this.conditions, MEMBERSHIP_TIER_CONDITIONS.TICKETS)
      },
      requiresMovies () {
        return _.includes(this.conditions, MEMBERSHIP_TIER_CONDITIONS.MOVIES)
      },
      requiresPoints () {
        return _.includes(this.conditions, MEMBERSHIP_TIER_CONDITIONS.POINTS)
      },
      requiresPointsBalance () {
        return _.includes(this.conditions, MEMBERSHIP_TIER_CONDITIONS.POINTS_BALANCE)
      },
      activeMembershipVouchers () {
        return _.filter(this.membershipType.activeMembershipVouchers, membershipVoucher => {
          // Skip if conditions or membershipTierIds are missing/invalid
          if (_.isEmpty(membershipVoucher?.membershipTierIds)) {
            return false
          }
          // Vouchers with tier conditions (tier and tier_upgrade) disabled (false) apply to all tiers and are already
          // handled at the membership type level. We only want tier-specific vouchers here.
          if ((membershipVoucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.TIER] !== true) &&
            (membershipVoucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.TIER_UPGRADE] !== true)) {
            return false
          }
          // Vouchers with tier conditions (tier and tier_upgrade) enabled (true) are restricted to specific tiers.
          // Only include this voucher if the current tier ID is in the voucher's allowed tier list.
          return _.includes(membershipVoucher.membershipTierIds, this.id)
        })
      },
      conditionalMembershipVouchers () {
        return _.filter(this.activeMembershipVouchers, membershipVoucher => {
          if (!_.isObject(membershipVoucher.conditions)) return false

          return _.some(
            membershipVoucher.conditions,
            (value, key) =>
              _.includes(MEMBERSHIP_VOUCHER_CONDITIONS.TIER, key) &&
              Boolean(value)
          )
        })
      },
      unconditionalMembershipVouchers () {
        return _.filter(this.activeMembershipVouchers, membershipVoucher => {
          if (!_.isObject(membershipVoucher.conditions)) return true

          return _.every(
            membershipVoucher.conditions,
            (value, key) =>
              !_.includes(MEMBERSHIP_VOUCHER_CONDITIONS.TIER, key) || !value
          )
        })
      },
      unlimitedVouchers () {
        return _.filter(this.unconditionalMembershipVouchers, { limitRedemptions: false })
      },
      unlimitedTicketVouchers () {
        return _.filter(this.unlimitedVouchers, 'isTicket')
      },
      hasUnlimitedTicketVouchers () {
        return !!this.unlimitedTicketVouchers.length
      },
      annualVouchers () {
        return _.filter(this.unconditionalMembershipVouchers, 'isAnnual')
      },
      annualTicketVouchers () {
        return _.filter(this.annualVouchers, 'isTicket')
      },
      hasAnnualTicketVouchers () {
        return !!this.annualTicketVouchers.length
      },
      annualTicketVouchersCount () {
        return _.sumBy(this.annualTicketVouchers, 'redemptionsLimit')
      },
      monthlyVouchers () {
        return _.filter(this.unconditionalMembershipVouchers, 'isMonthly')
      },
      monthlyTicketVouchers () {
        return _.filter(this.monthlyVouchers, 'isTicket')
      },
      hasMonthlyTicketVouchers () {
        return !!this.monthlyTicketVouchers.length
      },
      monthlyTicketVouchersCount () {
        return _.sumBy(this.monthlyTicketVouchers, 'redemptionsLimit')
      },
      activeMembershipDiscounts () {
        return _.filter(this.membershipType.activeMembershipDiscounts, membershipDiscount => {
          // Skip if conditions or membershipTierIds are missing/invalid
          if (!membershipDiscount || !Array.isArray(membershipDiscount.membershipTierIds)) {
            return false
          }
          // Vouchers with tier condition disabled (false) apply to all tiers and are already
          // handled at the membership type level. We only want tier-specific vouchers here.
          if (membershipDiscount.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.TIER] !== true) {
            return false
          }
          // Vouchers with tier condition enabled (true) are restricted to specific tiers.
          // Only include this voucher if the current tier ID is in the voucher's allowed tier list.
          return _.includes(membershipDiscount.membershipTierIds, this.id)
        })
      },
      conditionalMembershipDiscounts () {
        return _.filter(this.activeMembershipDiscounts, membershipDiscount => {
          // ensure conditions is an object
          if (!_.isObject(membershipDiscount.conditions)) return false

          // check if any condition (except 'tier') is truthy
          return _.some(membershipDiscount.conditions, (value, key) =>
            _.includes(MEMBERSHIP_DISCOUNT_CONDITIONS.TIER, key) &&
            Boolean(value)
          )
        })
      },
      unconditionalMembershipDiscounts () {
        return _.filter(this.activeMembershipDiscounts, membershipDiscount => {
          // no conditions object at all = unconditional
          if (!_.isObject(membershipDiscount.conditions)) return true

          // keep only if no non-tier conditions exist
          return _.every(membershipDiscount.conditions, (value, key) =>
            !_.includes(MEMBERSHIP_DISCOUNT_CONDITIONS.TIER, key) || !value
          )
        })
      },
      unlimitedDiscounts () {
        return _.filter(this.unconditionalMembershipDiscounts, { limitRedemptions: false })
      },
      hasUnlimitedDiscounts () {
        return !!this.unlimitedDiscounts.length
      },
      limitedDiscounts () {
        return _.filter(this.unconditionalMembershipDiscounts, 'limitRedemptions')
      },
      hasLimitedDiscounts () {
        return !!this.limitedDiscounts.length
      },
      activeMembershipPoints () {
        return _.filter(this.membershipType.activeMembershipPoints, membershipPoint => {
          // Skip if conditions or membershipTierIds are missing/invalid
          if (!membershipPoint || !Array.isArray(membershipPoint.membershipTierIds)) {
            return false
          }
          // Vouchers with tier condition disabled (false) apply to all tiers and are already
          // handled at the membership type level. We only want tier-specific vouchers here.
          if (membershipPoint.conditions[MEMBERSHIP_POINT_CONDITIONS.TIER] !== true) {
            return false
          }
          // Vouchers with tier condition enabled (true) are restricted to specific tiers.
          // Only include this voucher if the current tier ID is in the voucher's allowed tier list.
          return _.includes(membershipPoint.membershipTierIds, this.id)
        })
      },
      conditionalPoints () {
        return _.filter(this.activeMembershipPoints, membershipPoint => {
          // ensure conditions is an object
          if (!_.isObject(membershipPoint.conditions)) return false

          // check if any condition (except 'tier') is truthy
          return _.some(membershipPoint.conditions, (value, key) =>
            _.includes(MEMBERSHIP_POINT_CONDITIONS.TIER, key) &&
            Boolean(value)
          )
        })
      },
      unconditionalPoints () {
        return _.filter(this.activeMembershipPoints, membershipPoint => {
          // no conditions object at all = unconditional
          if (!_.isObject(membershipPoint.conditions)) return true

          // keep only if no non-tier conditions exist
          return _.every(membershipPoint.conditions, (value, key) =>
            !_.includes(MEMBERSHIP_POINT_CONDITIONS.TIER, key) || !value)
        })
      },
      hasUnconditionalPoints () {
        return !!this.unconditionalPoints.length
      },
      unconditionalVisitPoints () {
        return _.sumBy(this.unconditionalPoints, 'visitPointsAccumulationRate')
      },
      unconditionalConcessionsPoints () {
        return _.sumBy(this.unconditionalPoints, 'concessionPointsAccumulationRate')
      },
      unconditionalTicketPricePoints () {
        return _.sumBy(this.unconditionalPoints, 'ticketPricePointsAccumulationRate')
      },
      unconditionalTicketValuePoints () {
        return _.sumBy(this.unconditionalPoints, 'ticketValuePointsAccumulationRate')
      },
      conditionsDescription () {
        const conditions = []
        if (this.isConditional) {
          if (this.requiresVisits) {
            conditions.push(this.i18n.tc('membership.visitTimes', this.conditionsRequirements.visits))
          }
          if (this.requiresTickets) {
            conditions.push(this.i18n.tc('membership.buyTickets', this.conditionsRequirements.tickets))
          }
          if (this.requiresMovies) {
            conditions.push(this.i18n.tc('membership.seeMovies', this.conditionsRequirements.movies))
          }
          if (this.requiresPoints) {
            conditions.push(this.i18n.tc('membership.earnPoints', this.conditionsRequirements.points))
          }
          if (this.requiresPointsBalance) {
            conditions.push(this.i18n.tc('membership.havePointsBalance', { count: this.conditionsRequirements.points_balance }))
          }
        }
        return _.startCase(conditions.join(', and')) + ` ${this.membershipType.conditionsTerm === MEMBERSHIP_TIER_CONDITIONS_TERMS.MONTHS
          ? this.i18n.t('membership.forEachMonth', { name: this.name })
          : this.i18n.t('membership.eachYear', { name: this.name })}`
      },
      formattedConditions () {
        const conditions = _.map(this.conditions, condition => _.startCase(condition))

        if (this.requiresVisits) {
          _.remove(conditions, condition => condition === _.startCase(MEMBERSHIP_TIER_CONDITIONS.VISITS))
          conditions.push(this.i18n.tc('membership.numberVisitEachTerm', this.conditionsRequirements.visits, { count: this.conditionsRequirements.visits, term: _.startCase(this.membershipType.conditionsTerm).slice(0, -1) }))
        }
        if (this.requiresTickets) {
          _.remove(conditions, condition => condition === _.startCase(MEMBERSHIP_TIER_CONDITIONS.TICKETS))
          conditions.push(this.i18n.tc('membership.numberTicketEachTerm', this.conditionsRequirements.tickets, { count: this.conditionsRequirements.tickets, term: _.startCase(this.membershipType.conditionsTerm).slice(0, -1) }))
        }
        if (this.requiresMovies) {
          _.remove(conditions, condition => condition === _.startCase(MEMBERSHIP_TIER_CONDITIONS.MOVIES))
          conditions.push(this.i18n.tc('membership.numberMovieEachTerm', this.conditionsRequirements.movies, { count: this.conditionsRequirements.movies, term: _.startCase(this.membershipType.conditionsTerm).slice(0, -1) }))
        }
        if (this.requiresPoints) {
          _.remove(conditions, condition => condition === _.startCase(MEMBERSHIP_TIER_CONDITIONS.POINTS))
          conditions.push(this.i18n.tc('membership.numberPointEachTerm', this.conditionsRequirements.points, { count: this.conditionsRequirements.points, term: _.startCase(this.membershipType.conditionsTerm).slice(0, -1) }))
        }
        if (this.requiresPointsBalance) {
          _.remove(conditions, condition => condition === _.startCase(MEMBERSHIP_TIER_CONDITIONS.POINTS_BALANCE))
          conditions.push(this.i18n.tc('membership.numberPointsBalanceEachTerm', this.conditionsRequirements.points_balance, { count: this.conditionsRequirements.points_balance, term: _.startCase(this.membershipType.conditionsTerm).slice(0, -1) }))
        }

        return conditions
      }
    }
  }
}
